import { Suspense, useState, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { RightSidebar } from './RightSidebar';
import { PageWrapper } from './PageWrapper';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { InactivityBanner } from '@/components/auth/InactivityBanner';
import { TrialCountdownBanner } from '@/components/billing/TrialCountdownBanner';
import { useAuth } from '@/hooks/useAuth';
import { useInactivityTimer } from '@/hooks/useInactivityTimer';
import { authApi } from '@/api/authApi';
import { TechnicianUtilizationModal } from '@/components/telemetry/TechnicianUtilizationModal';
import { BayUtilizationModal } from '@/components/telemetry/BayUtilizationModal';
import { PartsUtilizationModal } from '@/components/telemetry/PartsUtilizationModal';

export default function AppShell() {
  const { switchUser } = useAuth();
  const [idleWarning, setIdleWarning] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showTechModal, setShowTechModal] = useState(false);
  const [showBayModal, setShowBayModal] = useState(false);
  const [showPartsModal, setShowPartsModal] = useState(false);

  const handleWarn    = useCallback(() => setIdleWarning(true), []);
  const handleTimeout = useCallback(() => { setIdleWarning(false); switchUser(); }, [switchUser]);
  const handleDismiss = useCallback(() => setIdleWarning(false), []);
  const { reset }     = useInactivityTimer(handleWarn, handleTimeout, handleDismiss);

  const handleStay = useCallback(() => {
    setIdleWarning(false);
    authApi.heartbeat().catch(() => {});
    reset();
  }, [reset]);

  return (
    <div className="flex h-screen overflow-hidden bg-background-light dark:bg-background-dark">
      {idleWarning && <InactivityBanner onStay={handleStay} />}
      {/* Left sidebar — always visible on desktop; toggleable on mobile */}
      <div className={`${sidebarOpen ? 'block' : 'hidden'} lg:block`}>
        <Sidebar />
      </div>
      <div className="flex flex-col flex-1 overflow-hidden min-w-0">
        <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
        <TrialCountdownBanner />
        <PageWrapper>
          <Suspense fallback={<SpinnerOverlay />}>
            <Outlet />
          </Suspense>
        </PageWrapper>
        {/* Right sidebar — moves below main content on tablets */}
        <div className="lg:hidden border-t border-slate-200 dark:border-slate-700 overflow-auto">
          <RightSidebar
            onTechClick={() => setShowTechModal(true)}
            onBayClick={() => setShowBayModal(true)}
            onPartsClick={() => setShowPartsModal(true)}
          />
        </div>
      </div>
      {/* Right sidebar — beside content on desktop */}
      <div className="hidden lg:block">
        <RightSidebar
          onTechClick={() => setShowTechModal(true)}
          onBayClick={() => setShowBayModal(true)}
          onPartsClick={() => setShowPartsModal(true)}
        />
      </div>

      {/* Telemetry Modals */}
      <TechnicianUtilizationModal isOpen={showTechModal} onClose={() => setShowTechModal(false)} />
      <BayUtilizationModal isOpen={showBayModal} onClose={() => setShowBayModal(false)} />
      <PartsUtilizationModal isOpen={showPartsModal} onClose={() => setShowPartsModal(false)} />
    </div>
  );
}
